/**
 * سربرگِ «آدرس‌ها» — آدرسِ رویدادها کجاست و کجا به درد نمی‌خورد (WHOSUP-201).
 *
 * ═══ چرا وجود دارد ═══
 *
 * آدرسِ بیشترِ رویدادها رشته‌ای است که کرالر از دلِ صفحه‌ی یک سایتِ غریبه
 * بیرون کشیده. هیچ فرمی پرش نکرده و از نظرِ تایپ‌اسکریپت یک `string`ِ سالم
 * است، پس هیچ تستی نگاهش نمی‌کند. این صفحه همان نگاه است:
 *
 *   • چند رویداد آدرس دارند و چندتا مختصات؟
 *   • چندتا «آدرس» در واقع آدرس نیستند («TBA»، «Online»، یک لینک)؟
 *   • چندتا ناقص‌اند — بی‌پلاک، بی‌کدپستی، بی‌استان؟
 *   • کدام‌ها **یک جا**اند و چند جور نوشته شده‌اند؟
 *   • کدام شهر و کدام منبع بدترینشان را می‌دهد؟
 *
 * ═══ چرا «کاربر نمی‌بیندش» اولین چیزِ صفحه است ═══
 *
 * آدرس در سه جا می‌نشیند — `events.address`، `venues.address` (از راهِ
 * `venue_id`) و `event_occurrences.address` — ولی `mapEvent` در
 * `core/data/miscRepo.ts` فقط **اولی** را می‌خواند. پس آدرسی که
 * `propose_event_edit` روی ردیفِ مکان نوشته باشد، داریمش و کاربر هیچ‌وقت
 * نمی‌بیندش. این یک خرابیِ بی‌صداست و عددش باید بالای صفحه باشد، نه ته یک
 * جدول.
 *
 * (آدرسِ `event_occurrences` عمداً بیرونِ گزارش است: سوالش «این رویداد
 * کجاست» نیست، «این یک جلسه استثنائاً کجاست» است.)
 *
 * ═══ تقسیمِ کار ═══
 *
 * مایگریشنِ ۰۱۴۰ فقط **واقعیت** می‌دهد و هیچ‌جا نمی‌گوید یک آدرس خوب است یا
 * بد؛ حکم در `core/lib/addressReport.ts` است و با `npm test` آزمودنی —
 * همان تقسیمی که `sourceTest.ts` و `journeyReport.ts` دارند. این فایل فقط
 * رندر می‌کند.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import { describeError, reportError } from '@/core/lib/report';
import {
  ADDRESS_ISSUES,
  GEO_BANDS,
  GEO_BAND_HELP,
  buildAddressReport,
  geoBand,
  isSuspectPoint,
  type AddressBucket,
  type AddressReport,
  type AddressSample,
  type GeocodeCache,
  type GeoPoint,
  type Severity,
} from '@/core/lib/addressReport';
import { mapViewHtml } from '@/core/lib/mapView';
import { Hint } from '@/ui/Hint';
import { fmtNum as fmt } from '@/ui/Num';
import { AddressTable } from './AddressTable';
import { AliasGlossary } from './AliasGlossary';

const SEVERITY_LABEL: Record<Severity, string> = {
  fail: 'خراب', warn: 'مشکوک', info: 'خبر',
};

/**
 * ⚠ `info` عمداً رنگی نیست: نشانه‌ی قرمز/برجسته روی چیزی که قرار نیست کسی
 * درستش کند، همان چیزی است که بعدِ یک هفته کلِ ستون را بی‌معنی می‌کند.
 */
function Sev({ s }: { s: Severity }) {
  return (
    <span className={`chip${s === 'fail' ? ' warn' : ''}`}>
      {SEVERITY_LABEL[s]}
    </span>
  );
}

/** آدرس، بریده ولی کامل در title — بعضی‌شان یک پاراگرافند. */
function Addr({ value }: { value: string }) {
  const flat = value.replace(/\s+/g, ' ').trim();
  return (
    <span className="mono ltr" title={flat}>
      {flat.length > 56 ? `${flat.slice(0, 56)}…` : flat}
    </span>
  );
}

/** «۳ نمونه» زیرِ هر ایراد — بی‌نمونه، عدد فقط می‌گوید چیزی خراب است. */
function Samples({ rows }: { rows: AddressSample[] }) {
  if (rows.length === 0) return <span className="dim">—</span>;
  return (
    <>
      {rows.map((r, i) => (
        <div key={`${r.id}-${i}`}>
          <Addr value={r.address} />
          <span className="dim"> · {r.title}</span>
        </div>
      ))}
    </>
  );
}

/** جدولِ «به تفکیکِ …» — شهر و منبع دقیقاً یک شکل‌اند، پس یک کامپوننت. */
function Buckets({ title, help, head, rows }: {
  title: string; help: string; head: string; rows: AddressBucket[];
}) {
  return (
    <div className="card">
      <h2><Hint text={title} help={help} /></h2>
      {rows.length === 0 ? (
        <p className="dim" style={{ marginTop: 10 }}>هیچ رویدادِ زنده‌ای نیست.</p>
      ) : (
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>{head}</th>
                <th><Hint text="کشور" help="قاعده‌های کاناداییِ این صفحه (کدپستی، کدِ استان، مرزِ مختصات) فقط روی CA اجرا می‌شوند." /></th>
                <th>رویداد</th><th>با آدرس</th><th>با مختصات</th>
                <th>با مکان</th>
                <th><Hint text="آدرسِ یکتا" help="فاصله‌ی این با ستونِ «با آدرس» یعنی چند رویداد آدرسِ مشترک دارند." /></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.key}>
                  <td className="mono ltr">{b.key}</td>
                  <td className="mono ltr">{b.country ?? <span className="dim">—</span>}</td>
                  <td className="num">{fmt(b.events)}</td>
                  <td className="num">{b.with_address < b.events
                    ? <span className="bad">{fmt(b.with_address)}</span> : fmt(b.with_address)}</td>
                  <td className="num">{b.with_geo < b.events
                    ? <span className="bad">{fmt(b.with_geo)}</span> : fmt(b.with_geo)}</td>
                  <td className="num">{fmt(b.with_venue)}</td>
                  <td className="num">{fmt(b.addresses)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** مختصات، همیشه لاتین و با دقتِ ثابت — وگرنه ستون بالا و پایین می‌پرد. */
function Coord({ lat, lng }: { lat: number; lng: number }) {
  return <span className="mono ltr">{lat.toFixed(5)}, {lng.toFixed(5)}</span>;
}

/** فاصله تا مرکزِ شهر. `null` یعنی سنجیدنی نیست، و آن با «صفر» یکی نیست. */
function Km({ km }: { km: number | null }) {
  if (km == null) return <span className="dim" title={GEO_BAND_HELP.unmeasured}>—</span>;
  const band = geoBand(km);
  const text = `${fmt(Math.round(km))} km`;
  return band === 'far' || band === 'wild'
    ? <span className="err" title={GEO_BAND_HELP[band]}>{text}</span>
    : <span title={GEO_BAND_HELP[band]}>{text}</span>;
}

/**
 * نقشه‌ی ته صفحه، با فیلترِ شهر.
 *
 * ═══ چرا iframe و نه یک کامپوننتِ نقشه ═══
 *
 * چون این‌طوری هیچ وابستگیِ تازه‌ای به اپ اضافه نمی‌شود: صفحه‌ی نقشه یک
 * رشته‌ی HTML است که `core/lib/mapView.ts` می‌سازد و Leaflet را از CDN
 * می‌گیرد — همان کاری که فرمِ انتخابِ مکان در اپِ وب از قبل می‌کند
 * (`features/discover/LocationPicker.tsx`).
 *
 * `sandbox="allow-scripts"` عمدی است و `allow-same-origin` ندارد: قاب به
 * اسکریپت نیاز دارد (خودِ Leaflet) ولی به نشستِ ادمین نه.
 *
 * ⚠ فیلتر در ری‌اکت انجام می‌شود و نه داخلِ نقشه، پس هر بار عوض کردنش قاب را
 * از نو می‌سازد. این‌جا **خواسته** است: نقشه باید روی شهرِ تازه fit شود، نه
 * اینکه آدم را وسطِ اقیانوس رها کند.
 */
function GeoMap({ points }: { points: GeoPoint[] }) {
  const [city, setCity] = useState('~all');
  const [failed, setFailed] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);

  const cities = useMemo(() => {
    const seen = new Map<string, number>();
    for (const p of points) seen.set(p.city, (seen.get(p.city) ?? 0) + p.events);
    return [...seen.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [points]);

  const shown = useMemo(
    () => (city === '~all' ? points : points.filter((p) => p.city === city)),
    [points, city],
  );

  const html = useMemo(() => mapViewHtml(shown.map((p) => ({
    lat: p.lat,
    lng: p.lng,
    // نامِ مکان اولویت دارد؛ آدرس وقتی می‌آید که مکانی در کار نباشد.
    title: p.venue_name ?? p.address ?? p.sample_title,
    note: p.venue_name && p.address ? p.address : null,
    events: p.events,
    suspect: isSuspectPoint(p.km),
  }))), [shown]);

  // خطای داخلِ قاب هیچ کنسولی ندارد که کسی ببیندش؛ صفحه‌ی نقشه با
  // postMessage بیرونش می‌دهد تا کادرِ خالی بی‌توضیح نماند.
  useEffect(() => {
    setFailed(false);
    const onMessage = (e: MessageEvent) => {
      if (frame.current && e.source !== frame.current.contentWindow) return;
      try {
        const m = JSON.parse(String(e.data)) as { type?: string; message?: string };
        if (m.type === 'error') {
          setFailed(true);
          reportError('admin.addresses.map', m.message ?? 'js error');
        }
      } catch { /* پیامِ کسِ دیگر */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [html]);

  const suspects = shown.filter((p) => isSuspectPoint(p.km)).length;

  return (
    <div className="card">
      <h2>
        <Hint text="نقشه"
          help="هر دایره یک نقطه‌ی یکتاست و نه یک رویداد؛ بزرگی‌اش از شمارِ رویدادهای همان نقطه می‌آید. روی دایره بزن تا مکان و مختصاتش را ببینی." />
      </h2>

      <div className="filters" style={{ marginTop: 8, marginBottom: 10 }}>
        <select value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="~all">همه‌ی شهرها ({fmt(points.length)} نقطه)</option>
          {cities.map(([c, events]) => (
            <option key={c} value={c}>{c} — {fmt(events)} رویداد</option>
          ))}
        </select>
        <span className="spacer" style={{ flex: 1 }} />
        <span className="dim">
          {fmt(shown.length)} نقطه
          {suspects > 0 ? <> · <span className="err">{fmt(suspects)} تای قرمز</span></> : null}
        </span>
      </div>

      <p className="note" style={{ marginBottom: 10 }}>
        قرمز یعنی بیش از {fmt(GEO_BANDS.mid)} کیلومتر از مرکزِ شهرِ خودش دور
        است — گاهی واقعی (مزرعه، پارک) و گاهی یعنی ژئوکدر شهرِ اشتباهی را
        گرفته. کاشی‌ها از OpenStreetMap می‌آیند و هیچ کلیدی نمی‌خواهند.
      </p>

      {shown.length === 0 ? (
        <p className="dim">این شهر هیچ نقطه‌ای ندارد.</p>
      ) : (
        <>
          {/* کلیدِ شهر است تا عوض کردنِ فیلتر واقعاً قاب را از نو بسازد و
              نقشه روی شهرِ تازه fit شود. */}
          <iframe key={city} ref={frame} title="نقشه‌ی مختصاتِ رویدادها"
            srcDoc={html} sandbox="allow-scripts"
            style={{
              width: '100%', height: 460, border: '1px solid var(--line)',
              borderRadius: 8, display: 'block',
            }}
            onError={() => setFailed(true)} />
          {failed ? (
            <p className="err" style={{ marginTop: 8 }}>
              نقشه بالا نیامد — احتمالاً Leaflet از CDN لود نشد.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/** کشِ ژئوکدینگِ کرالر: چه پرسید، چه گرفت، و چه چیزی را رد کرد. */
function GeocodeCacheCard({ cache }: { cache: GeocodeCache | null }) {
  const [open, setOpen] = useState(false);

  if (!cache) {
    return (
      <div className="card">
        <h2>ژئوکدینگِ کرالر</h2>
        <p className="dim" style={{ marginTop: 10 }}>
          این دیتابیس جدولِ <span className="mono ltr">crawler.building_geocodes</span> را
          ندارد. آن جدول مالِ مخزنِ event_crawler است و تا وقتی کرالر روی این
          دیتابیس نچرخیده باشد ساخته نمی‌شود.
        </p>
      </div>
    );
  }

  const share = cache.total > 0
    ? `${Math.round((cache.generated / cache.total) * 100)}٪` : '—';

  return (
    <div className="card">
      <h2>
        <Hint text="ژئوکدینگِ کرالر"
          help="کرالر برای هر ساختمان یک بار از یک مدل مختصات می‌پرسد و جواب را در crawler.building_geocodes نگه می‌دارد. این جدول کشِ همان پرسش‌هاست — نه مختصاتِ رویدادها، که بالاتر شمرده شده‌اند." />
      </h2>

      <div className="grid n4" style={{ marginTop: 10 }}>
        <div className="card stat">
          <div className="n">{fmt(cache.total)}</div>
          <div className="k">
            <Hint text="ساختمانِ پرسیده‌شده"
              help={`در ${fmt(cache.cities)} شهر، با ${fmt(cache.versions)} نسخه‌ی پرامپت.`} />
          </div>
        </div>
        <div className="card stat good">
          <div className="n">{fmt(cache.generated)}</div>
          <div className="k">جواب گرفت · {share}</div>
        </div>
        <div className={`card stat${cache.rejected > 0 ? ' warn' : ''}`}>
          <div className="n">{fmt(cache.rejected)}</div>
          <div className="k">
            <Hint text="رد شد"
              help="یا مدل جواب نداد، یا جوابش از فیلترها رد نشد. دلیل‌هایش در جدولِ زیر." />
          </div>
        </div>
        <div className={`card stat${cache.with_point !== cache.generated ? ' warn' : ''}`}>
          <div className="n">{fmt(cache.with_point)}</div>
          <div className="k">
            <Hint text="نقطه دارد"
              help="باید با «جواب گرفت» برابر باشد. نابرابری یعنی ردیفی generated علامت خورده ولی lat/lng ندارد." />
          </div>
        </div>
      </div>

      <div className="grid n3" style={{ marginTop: 12 }}>
        <div>
          <h3>دلیل</h3>
          <table className="tbl">
            <tbody>
              {cache.by_reason.map((r) => (
                <tr key={r.kind}>
                  <td className="mono ltr">{r.kind}</td>
                  <td className="num">{fmt(r.n)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3>مدل</h3>
          <table className="tbl">
            <tbody>
              {cache.by_model.map((m) => (
                <tr key={m.key}>
                  <td className="mono ltr" title={m.key}>
                    {m.key.length > 28 ? `${m.key.slice(0, 28)}…` : m.key}
                  </td>
                  <td className="num">{fmt(m.generated)} / {fmt(m.n)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3>شهر</h3>
          <table className="tbl">
            <tbody>
              {cache.by_city.map((c) => (
                <tr key={c.key}>
                  <td className="mono ltr">{c.key}</td>
                  <td className="num">{fmt(c.generated)} / {fmt(c.n)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <button type="button" className="linkbtn" style={{ marginTop: 10 }}
        onClick={() => setOpen((v) => !v)}>
        {open ? 'بستنِ آخرین پرسش‌ها' : `آخرین ${fmt(cache.recent.length)} پرسش`}
      </button>

      {open ? (
        <div className="scrollx" style={{ marginTop: 8 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th><Hint text="کلید" help="addr = آدرسِ یکسان‌شده · venue = نامِ مکان. کرالر با همین کلید دوباره نمی‌پرسد." /></th>
                <th>شهر</th><th>مقدار</th><th>مختصات</th><th>حکم</th>
                <th><Hint text="مطمئن" help="خودِ مدل گفته مطمئن است یا نه — جدا از اینکه کرالر قبولش کرده باشد." /></th>
                <th>دلیل</th><th>چه چیزی را ژئوکد کرد</th><th>کِی</th>
              </tr>
            </thead>
            <tbody>
              {cache.recent.map((r, i) => (
                <tr key={`${r.key_kind}:${r.city}:${r.key_value}:${i}`}>
                  <td className="mono ltr">{r.key_kind}</td>
                  <td className="mono ltr">{r.city}</td>
                  <td className="mono ltr" title={r.key_value}>
                    {r.key_value.length > 34 ? `${r.key_value.slice(0, 34)}…` : r.key_value}
                  </td>
                  <td>{r.lat != null && r.lng != null
                    ? <Coord lat={r.lat} lng={r.lng} /> : <span className="dim">—</span>}</td>
                  <td>
                    <span className={`chip${r.confidence === 'rejected' ? ' warn' : ''}`}>
                      {r.confidence}
                    </span>
                  </td>
                  <td className="num">{r.certain == null
                    ? <span className="dim">—</span> : (r.certain ? '✓' : '✕')}</td>
                  <td className="mono ltr">{r.reasons.join(' · ')}</td>
                  <td className="dim">{r.matched ?? '—'}</td>
                  <td className="num">{formatDateTime(r.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/**
 * بخشِ مختصات — از «چقدرش را داریم» تا «کجایش غلط است»، و بعد نقشه.
 *
 * ⚠ این بخش تا وقتی ۰۱۴۰ تنها بود اصلاً وجود نداشت، چون از ۳۶۵۶ رویداد
 * فقط ۶۱ تا مختصات داشتند و حرفی برای گفتن نبود. حالا که کرالر ژئوکدینگ
 * دارد، سوال عوض شده: دیگر «داریم یا نه» نیست، «درست است یا نه» است.
 */
function GeoSection({ rep, liveEvents }: { rep: AddressReport; liveEvents: number }) {
  const g = rep.geo;
  const t = g.totals;
  const noGeo = liveEvents - t.with_geo;
  const wild = g.bands.find((b) => b.band === 'wild');

  if (t.with_geo === 0) {
    return (
      <div className="card">
        <h2>مختصات</h2>
        <p className="dim" style={{ marginTop: 10 }}>
          هیچ رویدادِ زنده‌ای مختصات ندارد. مختصات از ردیفِ مکان می‌آید
          (<span className="mono ltr">venues.lat/lng</span>) و آن را کرالر پر
          می‌کند؛ خالی بودنِ کامل یعنی ژئوکدینگ روی این دیتابیس هنوز نچرخیده.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <h2>
          <Hint text="مختصات"
            help="مختصات از venues.lat/lng می‌آید و اگر نبود از خودِ ردیفِ رویداد — همان ترتیبی که mapEvent در اپ دارد. برعکسِ آدرس، که اپ فقط events.address را نشان می‌دهد." />
        </h2>

        <div className="grid n4" style={{ marginTop: 10 }}>
          <div className={`card stat${noGeo > 0 ? ' warn' : ''}`}>
            <div className="n">{fmt(noGeo)}</div>
            <div className="k">
              <Hint text="بی‌مختصات"
                help={`از ${fmt(liveEvents)} رویدادِ زنده. ${fmt(t.with_geo)} تا نقطه دارند.`} />
            </div>
          </div>
          <div className="card stat">
            <div className="n">{fmt(t.distinct_points)}</div>
            <div className="k">
              <Hint text="نقطه‌ی یکتا"
                help={`${fmt(t.with_geo)} رویداد روی ${fmt(t.distinct_points)} نقطه نشسته‌اند — یک سالن ده‌ها رویداد دارد و یک نقطه. شلوغ‌ترین نقطه ${fmt(rep.geo.biggestPile)} رویداد دارد.`} />
            </div>
          </div>
          <div className={`card stat${(wild?.events ?? 0) > 0 ? ' warn' : ''}`}>
            <div className="n">{fmt(wild?.events ?? 0)}</div>
            <div className="k">
              <Hint text="نقطه‌ی پرت" help={GEO_BAND_HELP.wild} />
            </div>
          </div>
          <div className={`card stat${t.on_row > 0 ? ' warn' : ''}`}>
            <div className="n">{fmt(t.on_row)}</div>
            <div className="k">
              <Hint text="روی ردیفِ رویداد"
                help={`باید صفر بماند: کرالر مختصات را روی ردیفِ مکان می‌نویسد، نه روی رویداد. ${fmt(t.on_venue)} تا روی مکان‌اند.`} />
            </div>
          </div>
        </div>

        {t.points_capped ? (
          <p className="err" style={{ marginTop: 10 }}>
            فهرستِ نقطه‌ها به سقف خورده: از {fmt(t.distinct_points)} نقطه فقط
            {' '}{fmt(t.listed_points)} تا تحلیل و روی نقشه گذاشته شده.
          </p>
        ) : null}

        <h3 style={{ marginTop: 14 }}>
          <Hint text="فاصله تا مرکزِ شهر"
            help="مرکز از جدولِ cities می‌آید. مرزها (۵ / ۲۵ / ۱۰۰ کیلومتر) در core/lib/addressReport.ts‌اند و نه در SQL، چون حکم‌اند نه حساب — عوض کردنشان مایگریشن نمی‌خواهد." />
        </h3>
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr><th>باند</th><th>نقطه</th><th>رویداد</th><th>سهم</th><th>دورترین</th></tr>
            </thead>
            <tbody>
              {g.bands.map((b) => (
                <tr key={b.band}>
                  <td>
                    <Hint text={b.label} help={b.help} />
                  </td>
                  <td className="num">{fmt(b.points)}</td>
                  <td className={`num${b.band === 'wild' && b.events > 0 ? ' bad' : ''}`}>
                    {fmt(b.events)}
                  </td>
                  <td className="num">{t.with_geo > 0
                    ? `${Math.round((b.events / t.with_geo) * 100)}٪` : '—'}</td>
                  <td className="num">{b.maxKm == null
                    ? <span className="dim">—</span> : `${fmt(Math.round(b.maxKm))} km`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>
          <Hint text="دورترین نقطه‌ها"
            help={`بیش از ${GEO_BANDS.mid} کیلومتر از مرکزِ شهرِ خودش. بعضی‌شان واقعی‌اند و بعضی‌شان یعنی ژئوکدر شهرِ هم‌نامِ کشورِ دیگری را گرفته.`} />
        </h2>
        {g.outliers.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>
            هر نقطه‌ای داخلِ {fmt(GEO_BANDS.mid)} کیلومتریِ شهرِ خودش است.
          </p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>فاصله</th><th>شهر</th><th>کشور</th><th>مکان</th>
                  <th>آدرس</th><th>مختصات</th><th>رویداد</th><th>نمونه</th>
                </tr>
              </thead>
              <tbody>
                {g.outliers.map((p) => (
                  <tr key={p.sample_id}>
                    <td className="num"><Km km={p.km} /></td>
                    <td className="mono ltr">{p.city}</td>
                    <td className="mono ltr">{p.country ?? <span className="dim">—</span>}</td>
                    <td>{p.venue_name ?? <span className="dim">—</span>}</td>
                    <td>{p.address
                      ? <Addr value={p.address} /> : <span className="dim">—</span>}</td>
                    <td><Coord lat={p.lat} lng={p.lng} /></td>
                    <td className="num">{fmt(p.events)}</td>
                    <td className="dim">{p.sample_title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>
          <Hint text="شلوغ‌ترین نقطه‌ها"
            help="یک نقطه با ده‌ها رویداد معمولاً یک سالنِ واقعی است و خرابی نیست. ولی ستونِ «مکان» اگر بیش از یکی باشد یعنی چند ردیفِ venue روی یک نقطه نشسته‌اند — همان چیزی که «یک آدرس، چند مکان» بالاتر می‌شمارد." />
        </h2>
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>رویداد</th><th>شهر</th><th>مکان</th>
                <th><Hint text="ردیفِ venue" help="چند ردیفِ متفاوتِ venue روی همین یک نقطه. بیش از یکی یعنی تکرار." /></th>
                <th>آدرسِ یکتا</th><th>مختصات</th><th>فاصله</th>
              </tr>
            </thead>
            <tbody>
              {g.piles.map((p) => (
                <tr key={p.sample_id}>
                  <td className="num">{fmt(p.events)}</td>
                  <td className="mono ltr">{p.city}</td>
                  <td>{p.venue_name ?? <span className="dim">—</span>}</td>
                  <td className="num">{p.venues > 1
                    ? <span className="bad">{fmt(p.venues)}</span> : fmt(p.venues)}</td>
                  <td className="num">{fmt(p.addresses)}</td>
                  <td><Coord lat={p.lat} lng={p.lng} /></td>
                  <td className="num"><Km km={p.km} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <GeocodeCacheCard cache={t.cache} />

      <GeoMap points={rep.points} />
    </>
  );
}

export function AddressesTab() {
  const q = useQuery({ queryKey: ['eventAddresses'], queryFn: () => opsRepo.eventAddresses() });
  // فهرستِ «بدترین‌ها» بلند است و بیشترِ وقت‌ها آدم فقط سرش را می‌خواهد.
  const [allProblems, setAllProblems] = useState(false);

  // ⚠ تحلیل روی هزاران رشته می‌چرخد؛ بی‌این، هر رندرِ دوباره (باز و بسته
  // کردنِ همان فهرست) کلش را از نو حساب می‌کرد.
  const rep = useMemo(() => (q.data ? buildAddressReport(q.data) : null), [q.data]);

  if (q.isLoading) return <p className="dim">…</p>;
  // ⚠ نه String(q.error): خطای PostgREST یک شیءِ ساده است و String ازش
  // «[object Object]» می‌سازد — همان چیزی که یک بار روی سربرگِ «عکس‌ها»
  // دیده شد، وقتی مایگریشنش روی دیتابیس نبود.
  if (q.error) return <div className="card"><p className="err">{describeError(q.error)}</p></div>;
  if (!rep) return null;

  const t = rep.totals;
  const noAddress = t.live_events - t.with_address;
  const noGeo = t.live_events - t.with_geo;
  const problems = allProblems ? rep.problems : rep.problems.slice(0, 15);

  if (t.live_events === 0) {
    return (
      <>
      <div className="card">
        <h2>آدرس‌ها</h2>
        <p className="dim" style={{ marginTop: 10 }}>
          هیچ رویدادِ زنده‌ای نیست (published و merge‌نشده)، پس آدرسی هم برای
          تحلیل نیست. {t.excluded > 0
            ? `${fmt(t.excluded)} ردیفِ دیگر هست ولی همه rejected یا merge‌شده‌اند.`
            : 'جدولِ رویدادها خالی است.'}
        </p>
      </div>
      {/* جدولِ تفکیک و واژه‌نامه هیچ‌کدام به رویدادِ زنده بند نیستند. */}
      <AddressTable />
      <AliasGlossary />
      </>
    );
  }

  return (
    <>
      <p className="note legend">
        آدرس در سه جا می‌نشیند — روی خودِ ردیفِ رویداد، روی ردیفِ مکانش، و روی
        تک‌جلسه‌های یک سری — ولی اپ فقط <b>اولی</b> را نشان می‌دهد. این صفحه
        دو تای اول را با هم می‌خواند و می‌گوید کدام آدرس را داریم و کاربر
        نمی‌بیندش. برشِ کار: {fmt(t.live_events)} رویدادِ زنده؛
        {' '}{fmt(t.excluded)} ردیفِ rejected/merged شمرده نشده.
        {t.outside_ca > 0 ? (
          <>
            {' '}از آن {fmt(t.live_events)} تا، {fmt(t.outside_ca)} رویداد شهرِ
            غیرکانادایی دارند و قاعده‌های کاناداییِ این صفحه — کدپستی، کدِ
            استان، مرزِ مختصات — رویشان اجرا <b>نمی‌شود</b>.
          </>
        ) : null}
      </p>

      {/* هر ساختمان یک ردیف، با اجزایش (WHOSUP-277) — اولِ صفحه، چون
          جایی است که ادمین واقعاً ستون‌های غلط را علامت می‌زند. */}
      <AddressTable />

      {/* ═══ اول، چیزی که مستقیم به چشمِ کاربر می‌خورد ═══ */}
      <div className="grid n4">
        <div className={`card stat${noAddress > 0 ? ' warn' : ''}`}>
          <div className="n">{fmt(noAddress)}</div>
          <div className="k">
            <Hint text="بی‌آدرس"
              help={`از ${fmt(t.live_events)} رویدادِ زنده. نه روی ردیف آدرسی دارند، نه روی مکانشان.`} />
          </div>
        </div>
        <div className={`card stat${t.venue_only > 0 ? ' warn' : ''}`}>
          <div className="n">{fmt(t.venue_only)}</div>
          <div className="k">
            <Hint text="داریم، نشان نمی‌دهیم"
              help={'آدرس فقط روی ردیفِ venue است و اپ events.address را نشان می‌دهد، '
                + `پس کاربر هیچ آدرسی نمی‌بیند. ${fmt(t.row_address)} رویداد آدرسی دارند که واقعاً دیده می‌شود.`} />
          </div>
        </div>
        <div className={`card stat${noGeo > 0 ? ' warn' : ''}`}>
          <div className="n">{fmt(noGeo)}</div>
          <div className="k">
            <Hint text="بی‌مختصات"
              help={`روی نقشه نمی‌نشینند. ${fmt(t.live_events - t.with_venue)} رویداد هم به هیچ ردیفِ venue وصل نیستند.`} />
          </div>
        </div>
        <div className={`card stat${t.conflicts > 0 ? ' warn' : ''}`}>
          <div className="n">{fmt(t.conflicts)}</div>
          <div className="k">
            <Hint text="با مکانش نمی‌خوانَد"
              help="هم روی ردیف آدرس هست هم روی مکان، و یکی نیستند. کاربر آنِ روی ردیف را می‌بیند؛ دست‌کم یکی کهنه است." />
          </div>
        </div>
      </div>

      {/* ═══ بعد، کیفیتِ خودِ رشته‌ها ═══ */}
      <div className="card">
        <h2>
          <Hint text="کیفیتِ رشته‌ها"
            help={`${fmt(t.distinct_addresses)} رشته‌ی یکتا (آدرس + شهر). یک رشته «خراب» است اگر `
              + 'دست‌کم یک ایرادِ شدید داشته باشد، «مشکوک» اگر فقط هشدار، و وگرنه سالم. '
              + '⚠ یک ایرادِ فراگیر (مثلاً نبودِ مختصات روی تقریباً همه) تقریباً همه‌چیز '
              + 'را به ستونِ «مشکوک» می‌برد؛ کدام ایراد است، در جدولِ زیر سطرِ اولِ هشدارهاست.'} />
        </h2>
        {t.capped ? (
          <p className="err" style={{ marginTop: 6 }}>
            فهرست به سقف خورده: از {fmt(t.distinct_addresses)} رشته فقط
            {' '}{fmt(t.listed)} تا تحلیل شده. شمارش‌های بالا کامل‌اند و این
            سقف را نمی‌بینند.
          </p>
        ) : null}
        <div className="grid n3" style={{ marginTop: 10 }}>
          <div className="card stat good">
            <div className="n">{fmt(rep.quality.cleanAddresses)}</div>
            <div className="k">سالم · {fmt(rep.quality.cleanEvents)} رویداد</div>
          </div>
          <div className="card stat">
            <div className="n">{fmt(rep.quality.warnAddresses)}</div>
            <div className="k">مشکوک · {fmt(rep.quality.warnEvents)} رویداد</div>
          </div>
          <div className="card stat warn">
            <div className="n">{fmt(rep.quality.failAddresses)}</div>
            <div className="k">خراب · {fmt(rep.quality.failEvents)} رویداد</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>
          <Hint text="ایرادها"
            help="یک رشته می‌تواند چند ایراد داشته باشد، پس جمعِ این ستون از شمارِ رشته‌ها بیشتر می‌شود. ترتیب بر اساسِ شمارِ رویدادِ درگیر است و نه شمارِ رشته — یک ایرادِ نادر روی آدرسی با صد رویداد مهم‌تر است." />
        </h2>
        {rep.issues.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>
            هیچ ایرادی پیدا نشد — هر {fmt(rep.quality.cleanAddresses)} رشته پلاک
            و شهر و استان و کدپستی دارند و مختصاتشان سرِ جاست.
          </p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>شدت</th><th>ایراد</th><th>رشته</th><th>رویداد</th><th>نمونه</th>
                </tr>
              </thead>
              <tbody>
                {rep.issues.map((i) => (
                  <tr key={i.code}>
                    <td><Sev s={i.severity} /></td>
                    <td><Hint text={i.label} help={i.help} /></td>
                    <td className="num">{fmt(i.addresses)}</td>
                    <td className="num">{fmt(i.events)}</td>
                    <td><Samples rows={i.samples} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>
          <Hint text="کاملیِ آدرس"
            help="از پنج تکه‌ی یک آدرسِ کانادایی — پلاک، خیابان، شهر، کدِ استان، کدپستی — چندتا در رشته هست. ۵ یعنی کامل." />
        </h2>
        {rep.completenessBase === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>
            هیچ رشته‌ی کانادایی‌ای در فهرست نیست.
          </p>
        ) : (
          <>
            <p className="dim" style={{ marginTop: 6 }}>
              فقط {fmt(rep.completenessBase)} رشته‌ی کانادایی؛ دو تکه از پنج
              تکه (کدپستی و کدِ استان) کانادایی‌اند و آدرسِ شهرِ خارجی همیشه
              سقفش ۳ می‌شد.
            </p>
            <div className="scrollx">
              <table className="tbl">
                <thead>
                  <tr><th>تکه‌ها</th><th>رشته</th><th>رویداد</th><th>سهم</th></tr>
                </thead>
                <tbody>
                  {[...rep.completeness].reverse().map((c) => (
                    <tr key={c.level}>
                      <td className="num">{c.level} از ۵</td>
                      <td className="num">{fmt(c.addresses)}</td>
                      <td className="num">{fmt(c.events)}</td>
                      <td className="num">
                        {Math.round((c.addresses / rep.completenessBase) * 100)}٪
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* یک جا با چند املا: تنها چیزی در این صفحه که با چشم پیدا نمی‌شود —
          دو ردیف که کنارِ هم در یک جدول هیچ‌وقت نمی‌نشینند. */}
      <div className="card">
        <h2>
          <Hint text="یک جا، چند املا"
            help="رشته‌هایی که بعدِ یکسان‌سازی (کوتاه‌نوشتِ نوعِ خیابان، جهت، اعراب، شهر و استان و کدپستی) یکی می‌شوند. تشخیصِ تکراری‌ها و نشستن روی نقشه هر دو از همین ضربه می‌خورند." />
        </h2>
        {rep.variants.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>
            هیچ جایی دو جور نوشته نشده.
          </p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr><th>شهر</th><th>املاها</th><th>رویداد</th><th>نوشته‌ها</th></tr>
              </thead>
              <tbody>
                {rep.variants.map((g) => (
                  <tr key={g.key}>
                    <td className="mono ltr">{g.city}</td>
                    <td className="num">{fmt(g.variants.length)}</td>
                    <td className="num">{fmt(g.events)}</td>
                    <td>
                      {g.variants.map((v) => (
                        <div key={v.address}>
                          <Addr value={v.address} />
                          <span className="dim"> · {fmt(v.events)} رویداد</span>
                          {v.geo_points > 1
                            ? <span className="err"> · {fmt(v.geo_points)} مختصاتِ متفاوت</span>
                            : null}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* واژه‌نامه کنارِ «چند املا» می‌نشیند: همان چیزی است که قرار است
          آن املاها را یکی کند (WHOSUP-245). */}
      <AliasGlossary />

      <div className="card">
        <h2>
          <Hint text="بدترین آدرس‌ها"
            help="هر رشته‌ای که دست‌کم یک ایرادِ واقعی دارد (ایرادهای «خبر» این‌جا نمی‌آیند)، از شدید به خفیف و بعد بر حسبِ شمارِ رویداد." />
        </h2>
        {rep.problems.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>هیچ رشته‌ای ایرادِ واقعی ندارد.</p>
        ) : (
          <>
            <div className="scrollx">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>شدت</th><th>آدرس</th><th>شهر</th><th>منبع</th><th>رویداد</th>
                    <th>ایرادها</th><th>نمونه</th>
                  </tr>
                </thead>
                <tbody>
                  {/* کلید نمونه‌ی رویداد است و نه آدرس: همان رشته می‌تواند دو
                      ردیف باشد (یکی روی رویداد، یکی روی مکان). */}
                  {problems.map((p) => (
                    <tr key={p.id}>
                      <td><Sev s={p.severity} /></td>
                      <td><Addr value={p.address} /></td>
                      <td className="mono ltr">{p.city}</td>
                      <td className="mono ltr">{p.sources.join(' · ')}</td>
                      <td className="num">{fmt(p.events)}</td>
                      <td>{p.issues.map((c) => ADDRESS_ISSUES[c].label).join('، ')}</td>
                      <td className="dim">{p.title}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rep.problems.length > problems.length || allProblems ? (
              <button type="button" className="linkbtn" style={{ marginTop: 8 }}
                onClick={() => setAllProblems((v) => !v)}>
                {allProblems
                  ? 'فقط ۱۵ تای اول'
                  : `همه‌ی ${fmt(rep.problems.length)} تا`}
              </button>
            ) : null}
          </>
        )}
      </div>

      <Buckets title="به تفکیکِ شهر" head="شهر" rows={rep.by_city}
        help="ستونِ city خودِ رویداد (که تریگرِ ۰۰۹۱ با city_id هم‌گامش نگه می‌دارد)، نه شهری که داخلِ رشته‌ی آدرس نوشته شده." />

      <Buckets title="به تفکیکِ منبع" head="منبع" rows={rep.by_source}
        help="ستونِ events.source — منبعِ کرالر، یا manual/user_submitted. این‌جاست که معلوم می‌شود یک سایتِ خاص آدرس نمی‌دهد." />

      <GeoSection rep={rep} liveEvents={t.live_events} />
    </>
  );
}
